// Raporlar — dönem/kırılım filtreleri, çağrı sayıları (bar + trend + temsilci kırılımı)
// ve bekleme süreleri (tembellik metriği + sonuç tipine göre dağılım).

import { useEffect, useMemo, useState } from 'react';
import { getReport, getStatusSystems, getTeamReport } from '../api';
import { brandLabel, tzOfBrands } from '../constants';
import { computePreset, downloadCSV, fmtDate, fmtHMS, fmtMS } from '../format';
import { useExpanded } from '../hooks';
import type { AgentReport, BrandId, Granularity, Lang, ReportPreset, StatusDef, TeamReport } from '../types';
import { CsvButton, cardStyle, dotStyle, EmptyState, Icon, SectionTitle, segStyle, Skeleton } from '../ui';

const SYS = getStatusSystems();
const ordFor = (lang: Lang): StatusDef[] => (lang === 'en' ? SYS.en : SYS.tr);

const PRESETS: [ReportPreset, string][] = [
  ['today', 'Bugün'],
  ['yesterday', 'Dün'],
  ['this_week', 'Bu Hafta'],
  ['last_week', 'Geçen Hafta'],
  ['this_month', 'Bu Ay'],
];

const GRANS: [Granularity, string][] = [
  ['daily', 'Günlük'],
  ['weekly', 'Haftalık'],
  ['monthly', 'Aylık'],
];

const GRAN_LABEL: Record<Granularity, string> = { daily: 'Günlük', weekly: 'Haftalık', monthly: 'Aylık' };

/** Cevaplama oranı eşikleri: %55+ iyi, %40+ orta, altı kötü. */
const rateColor = (rate: number) => (rate >= 55 ? '#37c46b' : rate >= 40 ? '#e0a23c' : '#ef6d6d');

const dateFilterStyle = {
  background: 'var(--inset)',
  border: '1px solid var(--border)',
  borderRadius: '9px',
  padding: '8px 10px',
  color: 'var(--text)',
  fontSize: '12.5px',
  outline: 'none',
  colorScheme: 'dark',
} as const;

const gridCols = '1fr 130px 88px 118px 96px 104px 40px';

/** Ölçülen oran: null (payda yok / ölçülmedi) → BOŞ, asla %0 (§6). */
const pctOrBlank = (n: number | null | undefined) => (n == null ? '' : '%' + n);

/** ms → "1,8 sn" (≥ 60 sn "d:ss") — Döngü kartıyla aynı biçim. */
const fmtCycleMs = (ms: number) =>
  ms >= 60_000 ? fmtHMS(ms / 1000) : (Math.round(ms / 100) / 10).toFixed(1).replace('.', ',') + ' sn';

interface WaitAgg {
  label: string;
  color: string;
  waitSum: number;
  cnt: number;
}

export default function Reports({ brands }: { brands: BrandId[] }) {
  // Dönem "bugün"ü marka diliminde çözülür (BetBooker'a Akra) — tarayıcı saati değil.
  const brandTz = tzOfBrands(brands);
  const [reportPreset, setReportPreset] = useState<ReportPreset>('today');
  const [reportGranularity, setReportGranularity] = useState<Granularity>('daily');
  const [reportStart, setReportStart] = useState(() => computePreset('today', brandTz).start);
  const [reportEnd, setReportEnd] = useState(() => computePreset('today', brandTz).end);
  const [reportRows, setReportRows] = useState<AgentReport[]>([]);
  // true ile başlar: ilk render'da iskelet çizilsin, "Veri yok" bir kare parlamasın.
  const [reportLoading, setReportLoading] = useState(true);
  // Ekip rakamları (döngü p50/p95 + havuzlanmış softphone ölçümü) — ayrı uç, aynı aralık/kapsam.
  const [team, setTeam] = useState<TeamReport | null>(null);
  const { toggle, isOpen } = useExpanded();

  const brandKey = brands.join(',');

  useEffect(() => {
    const range = reportPreset === 'custom' ? { start: reportStart, end: reportEnd } : computePreset(reportPreset, brandTz);
    if (!range.start || !range.end) return;
    let alive = true;
    setReportLoading(true);
    getReport(range, reportGranularity, brands, reportPreset === 'custom' ? undefined : reportPreset).then((rows) => {
      if (!alive) return;
      setReportRows(rows);
      setReportLoading(false);
    });
    // Ekip kartı yan bilgi: başarısızsa kart çizilmez, sayfanın geri kalanı etkilenmez.
    getTeamReport(range, brands, reportPreset === 'custom' ? undefined : reportPreset).then(
      (t) => { if (alive) setTeam(t); },
      () => { if (alive) setTeam(null); },
    );
    return () => {
      alive = false;
    };
  }, [reportPreset, reportStart, reportEnd, reportGranularity, brandKey]);

  const onPreset = (p: ReportPreset) => {
    const rng = computePreset(p, brandTz);
    setReportPreset(p);
    setReportStart(rng.start);
    setReportEnd(rng.end);
  };

  // Tarih kutusuna dokunmak dönemi serbest aralığa çevirir.
  const onReportStart = (v: string) => {
    setReportPreset('custom');
    setReportStart(v);
  };
  const onReportEnd = (v: string) => {
    setReportPreset('custom');
    setReportEnd(v);
  };

  const range = reportPreset === 'custom' ? { start: reportStart, end: reportEnd } : computePreset(reportPreset, brandTz);
  const reportRangeLabel = fmtDate(range.start) + ' – ' + fmtDate(range.end);
  const granLabel = GRAN_LABEL[reportGranularity];
  const lazThresholdLabel = fmtMS(180);

  const bars = useMemo(() => {
    const listAgg: Record<string, number> = {};
    reportRows.forEach((row) => row.lists.forEach((l) => (listAgg[l.list] = (listAgg[l.list] || 0) + l.total)));
    const entries = Object.keys(listAgg)
      .map((k) => ({ label: k, calls: listAgg[k] }))
      .sort((a, b) => b.calls - a.calls);
    return { entries, barMax: Math.max(1, ...entries.map((b) => b.calls)) };
  }, [reportRows]);

  const trend = useMemo(() => {
    const nb = reportRows.length ? Math.max(...reportRows.map((r) => r.buckets.length)) : 0;
    const series: { calls: number; label: string }[] = [];
    for (let i = 0; i < nb; i++) {
      let c = 0;
      let label = '';
      reportRows.forEach((row) => {
        if (row.buckets[i]) {
          c += row.buckets[i].calls;
          label = row.buckets[i].label;
        }
      });
      series.push({ calls: c, label });
    }
    const trendMax = Math.max(1, ...series.map((t) => t.calls));
    const W = 700;
    const padL = 12;
    const padR = 12;
    const padT = 14;
    const ih = 132;
    const iw = W - padL - padR;
    const pts = series.map((t, i) => {
      const x = nb <= 1 ? padL + iw / 2 : padL + (i / (nb - 1)) * iw;
      const y = padT + ih - (t.calls / trendMax) * ih;
      return { x, y, label: t.label };
    });
    const baseY = (padT + ih).toFixed(1);
    const trendPolyline = pts.map((p) => p.x.toFixed(1) + ',' + p.y.toFixed(1)).join(' ');
    const trendArea = pts.length
      ? 'M' +
        pts[0].x.toFixed(1) +
        ',' +
        baseY +
        ' L' +
        pts.map((p) => p.x.toFixed(1) + ',' + p.y.toFixed(1)).join(' L') +
        ' L' +
        pts[pts.length - 1].x.toFixed(1) +
        ',' +
        baseY +
        ' Z'
      : '';
    const trendDots = pts.map((p) => ({ cx: p.x.toFixed(1), cy: p.y.toFixed(1) }));
    const labelStep = Math.max(1, Math.ceil(nb / 8));
    const trendLabels = pts
      .filter((_, i) => i % labelStep === 0 || i === nb - 1)
      .map((p) => ({ x: p.x.toFixed(1), text: p.label }));
    return { trendMax, trendPolyline, trendArea, trendDots, trendLabels };
  }, [reportRows]);

  // Sonuç bazlı bekleme: temsilciler arası ortalama, sonuç adediyle ağırlıklandırılır.
  const waitByResult = useMemo(() => {
    const wbr: Record<string, WaitAgg> = {};
    reportRows.forEach((row) =>
      ordFor(row.lang).forEach((st) => {
        const occ = row.lists.reduce((a, l) => a + (l.results[st.key] || 0), 0);
        const w = row.wait.afterResult[st.key];
        if (occ > 0 && w) {
          if (!wbr[st.key]) wbr[st.key] = { label: st.label, color: st.color, waitSum: 0, cnt: 0 };
          wbr[st.key].waitSum += w.avgSec * occ;
          wbr[st.key].cnt += occ;
        }
      }),
    );
    return Object.keys(wbr)
      .map((k) => {
        const o = wbr[k];
        const avgSec = Math.round(o.waitSum / o.cnt);
        return { key: k, label: o.label, color: o.color, count: o.cnt, avg: fmtMS(avgSec), avgSec };
      })
      .sort((a, b) => b.avgSec - a.avgSec);
  }, [reportRows]);

  const onCsv1 = () => {
    const head = [
      'Temsilci', 'Departman', 'Marka', 'Toplam Cagri', 'Yanitlanan', 'Cevaplama %',
      'Arama (softphone)', 'Baglanan', 'Baglanma %', 'Ort Konusma', 'Toplam Konusma',
    ];
    // Ölçülmeyen alan BOŞ hücre (null → ''), asla 0 — §6.
    const v = (n: number | null | undefined) => (n == null ? '' : n);
    downloadCSV(
      'cagri-sayilari.csv',
      [head as (string | number)[]].concat(
        reportRows.map((r) => [
          r.name, r.department, r.brands.join('+'), r.totalCalls, r.answered, r.answerRate,
          v(r.dials), v(r.connected), v(r.connectRate), fmtHMS(r.avgTalkSec), fmtHMS(r.talkTotalSec),
        ]),
      ),
    );
  };

  const onCsv2a = () => {
    const head = ['Temsilci', 'Departman', 'Ort Bekleme', 'Uzun Bosluk >30dk', 'Esik Asildi'];
    downloadCSV(
      'tembellik-metrigi.csv',
      [head as (string | number)[]].concat(
        reportRows.map((r) => [
          r.name,
          r.department,
          fmtMS(r.wait.lazinessAvgSec),
          r.wait.longGaps,
          r.wait.lazinessAvgSec > r.wait.lazinessThresholdSec ? 'EVET' : 'HAYIR',
        ]),
      ),
    );
  };

  const onCsv2b = () => {
    const head = ['Sonuç', 'Ort Bekleme', 'Adet'];
    downloadCSV(
      'bekleme-dagilimi.csv',
      [head as (string | number)[]].concat(waitByResult.map((r) => [r.label, r.avg, r.count])),
    );
  };

  const loadingNow = reportLoading && reportRows.length === 0;
  const emptyReason =
    brands.length === 0
      ? 'Üstteki marka filtresinden en az bir marka seçin.'
      : 'Seçili dönem ve markalar için veri bulunamadı.';

  return (
    <>
      <div style={{ ...cardStyle, padding: '16px 18px', marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.06em', color: 'var(--faint)', marginRight: '2px' }}>
            DÖNEM
          </span>
          {PRESETS.map(([k, l]) => (
            <button key={k} onClick={() => onPreset(k)} style={segStyle(reportPreset === k)}>
              {l}
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '6px' }}>
            <input type="date" value={reportStart} onChange={(e) => onReportStart(e.target.value)} style={dateFilterStyle} />
            <span style={{ color: 'var(--faint)' }}>–</span>
            <input type="date" value={reportEnd} onChange={(e) => onReportEnd(e.target.value)} style={dateFilterStyle} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.06em', color: 'var(--faint)', marginRight: '2px' }}>
            KIRILIM
          </span>
          {GRANS.map(([k, l]) => (
            <button key={k} onClick={() => setReportGranularity(k)} style={segStyle(reportGranularity === k)}>
              {l}
            </button>
          ))}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--dim)', fontSize: '12.5px' }}>
            <Icon name="event" size={17} color="var(--faint)" />
            <span>{reportRangeLabel}</span>
          </div>
        </div>
      </div>

      {loadingNow && <Skeleton />}

      {!loadingNow && reportRows.length === 0 && <EmptyState icon="bar_chart" title="Veri yok" reason={emptyReason} />}

      {!loadingNow && reportRows.length > 0 && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: '14px', marginBottom: '20px' }}>
            <div style={{ ...cardStyle, padding: '16px 18px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Liste başına çağrı</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '14px' }}>Seçili dönem · tüm temsilciler</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                {bars.entries.map((bar) => (
                  <div key={bar.label} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span
                      style={{
                        width: '118px',
                        flex: 'none',
                        fontSize: '11px',
                        color: 'var(--dim)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {bar.label}
                    </span>
                    <div style={{ flex: 1, height: '14px', background: 'var(--inset)', borderRadius: '5px', overflow: 'hidden' }}>
                      <div style={{ width: (bar.calls / bars.barMax) * 100 + '%', height: '100%', background: '#3d8bff', borderRadius: '5px' }} />
                    </div>
                    <span
                      style={{
                        width: '46px',
                        flex: 'none',
                        textAlign: 'right',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: 'var(--text)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {bar.calls}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ ...cardStyle, padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Dönem trendi</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '8px' }}>Toplam çağrı · {granLabel}</div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--faint)' }}>
                  En yüksek <span style={{ color: 'var(--dim)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{trend.trendMax}</span>
                </div>
              </div>
              <svg viewBox="0 0 700 190" style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
                <path d={trend.trendArea} fill="rgba(61,139,255,.12)"></path>
                <polyline
                  points={trend.trendPolyline}
                  fill="none"
                  stroke="#3d8bff"
                  strokeWidth="2"
                  strokeLinejoin="round"
                  strokeLinecap="round"
                ></polyline>
                {trend.trendDots.map((d, i) => (
                  <circle key={i} cx={d.cx} cy={d.cy} r="2.6" style={{ fill: 'var(--bg)' }} stroke="#3d8bff" strokeWidth="1.6"></circle>
                ))}
                {trend.trendLabels.map((l, i) => (
                  <text key={i} x={l.x} y="184" style={{ fill: 'var(--faint)' }} fontSize="9" textAnchor="middle" fontFamily="Inter, sans-serif">
                    {l.text}
                  </text>
                ))}
              </svg>
            </div>
          </div>

          {/* Ekip softphone ölçümü + döngü süresi. Dönemde hiç oturum/ölçüm yoksa kart çizilmez;
              değeri olmayan kalem etiketiyle birlikte DÜŞER (§6). Oranlar havuzlanmış (tek bölme). */}
          {team && (team.dials != null || team.cycle) && (
            <div style={{ ...cardStyle, padding: '14px 18px', marginBottom: '18px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>Softphone ölçümü</div>
              <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginBottom: '12px' }}>Seçili dönem · ekip toplamı</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '22px' }}>
                {(
                  [
                    ['ARAMA', team.dials != null ? String(team.dials) : ''],
                    ['BAĞLANAN', team.connected != null ? String(team.connected) : ''],
                    ['BAĞLANMA', pctOrBlank(team.connectRate)],
                    ['ORT. KONUŞMA', fmtHMS(team.avgTalkSec)],
                    ['TOPLAM KONUŞMA', fmtHMS(team.talkTotalSec)],
                    ['DÖNGÜ P50', team.cycle ? fmtCycleMs(team.cycle.p50Ms) : ''],
                    ['DÖNGÜ P95', team.cycle ? fmtCycleMs(team.cycle.p95Ms) : ''],
                  ] as [string, string][]
                )
                  .filter(([, val]) => val !== '')
                  .map(([label, val]) => (
                    <div key={label}>
                      <div style={{ fontSize: '10px', color: 'var(--faint)', letterSpacing: '.05em', marginBottom: '3px' }}>{label}</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{val}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div style={{ ...cardStyle, marginBottom: '18px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '15px 18px', borderBottom: '1px solid var(--border)' }}>
              <SectionTitle n="1" title="Çağrı Sayıları" sub="Temsilci · liste × sonuç dağılımı" />
              <CsvButton onClick={onCsv1} />
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: gridCols,
                gap: '10px',
                padding: '10px 18px',
                borderBottom: '1px solid var(--border)',
                fontSize: '10.5px',
                fontWeight: 600,
                letterSpacing: '.05em',
                color: 'var(--faint)',
              }}
            >
              <span>TEMSİLCİ</span>
              <span>DEPARTMAN</span>
              <span style={{ textAlign: 'right' }}>TOPLAM</span>
              <span style={{ textAlign: 'right' }}>CEVAPLAMA</span>
              <span style={{ textAlign: 'right' }} title="Softphone: bağlanan / arama">BAĞLANMA</span>
              <span style={{ textAlign: 'right' }} title="Softphone: bağlanan çağrı başına konuşma">ORT. KONUŞMA</span>
              <span></span>
            </div>
            {reportRows.map((row) => (
              <div key={row.agentId} style={{ borderBottom: '1px solid var(--border)' }}>
                <div
                  onClick={() => toggle(row.agentId)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridCols,
                    gap: '10px',
                    padding: '12px 18px',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                    <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text)' }}>{row.name}</span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {row.brands.map((b) => (
                        <span
                          key={b}
                          style={{
                            fontSize: '9.5px',
                            fontWeight: 600,
                            color: 'var(--dim)',
                            background: 'var(--surface2)',
                            border: '1px solid var(--border)',
                            borderRadius: '5px',
                            padding: '2px 6px',
                          }}
                        >
                          {brandLabel(b)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--dim)' }}>{row.department}</span>
                  <span style={{ textAlign: 'right', fontSize: '14px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                    {row.totalCalls}
                  </span>
                  <span
                    style={{
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: '13.5px',
                      fontWeight: 700,
                      color: rateColor(row.answerRate),
                    }}
                  >
                    %{row.answerRate}
                  </span>
                  {/* Softphone ölçümü — dönemde oturumu olmayan temsilcide BOŞ (§6). */}
                  <span
                    title={row.dials != null ? 'Arama ' + row.dials + ' · bağlanan ' + (row.connected ?? 0) : undefined}
                    style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}
                  >
                    {pctOrBlank(row.connectRate)}
                  </span>
                  <span
                    title={row.talkTotalSec != null ? 'Toplam konuşma ' + fmtHMS(row.talkTotalSec) : undefined}
                    style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: '13px', color: 'var(--dim)' }}
                  >
                    {fmtHMS(row.avgTalkSec)}
                  </span>
                  <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Icon name={isOpen(row.agentId) ? 'expand_more' : 'chevron_right'} size={20} color="var(--faint)" />
                  </span>
                </div>
                {isOpen(row.agentId) && (
                  <div style={{ padding: '4px 18px 14px', display: 'flex', flexDirection: 'column', background: 'var(--row)' }}>
                    {row.lists.map((l) => (
                      <div
                        key={l.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          flexWrap: 'wrap',
                          padding: '8px 0',
                          borderBottom: '1px solid rgba(255,255,255,.04)',
                        }}
                      >
                        <span style={{ width: '150px', flex: 'none', display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <span
                            style={{
                              fontSize: '12px',
                              fontWeight: 600,
                              color: 'var(--text)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {l.list}
                          </span>
                          {l.manual && (
                            <span
                              style={{
                                fontSize: '9px',
                                fontWeight: 600,
                                letterSpacing: '.03em',
                                color: '#e0a23c',
                                background: 'rgba(224,162,60,.14)',
                                border: '1px solid rgba(224,162,60,.3)',
                                borderRadius: '5px',
                                padding: '1px 5px',
                                flex: 'none',
                              }}
                            >
                              manuel
                            </span>
                          )}
                        </span>
                        <span
                          style={{
                            fontSize: '13px',
                            fontWeight: 700,
                            color: 'var(--text)',
                            fontVariantNumeric: 'tabular-nums',
                            width: '38px',
                            flex: 'none',
                          }}
                        >
                          {l.total}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flex: 1 }}>
                          {ordFor(row.lang)
                            .filter((st) => (l.results[st.key] || 0) > 0)
                            .map((st) => (
                              <span key={st.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--dim)' }}>
                                <span style={dotStyle(st.color)}></span>
                                <span style={{ color: 'var(--text)', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{l.results[st.key]}</span>
                                {st.label}
                              </span>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '0 2px 14px' }}>
            <SectionTitle n="2" title="Bekleme Süreleri" sub="Çağrı sonrası bekleme analizi" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(360px,1fr))', gap: '14px' }}>
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Tembellik metriği</div>
                  <div style={{ fontSize: '11px', color: 'var(--faint)' }}>
                    Verimsiz sonuç sonrası ort. bekleme · eşik {lazThresholdLabel}
                  </div>
                </div>
                <CsvButton onClick={onCsv2a} small />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 96px 84px',
                  gap: '8px',
                  padding: '9px 16px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '.04em',
                  color: 'var(--faint)',
                }}
              >
                <span>TEMSİLCİ</span>
                <span style={{ textAlign: 'right' }}>ORT. BEKLEME</span>
                <span style={{ textAlign: 'right' }}>BOŞLUK</span>
              </div>
              <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                {reportRows.map((row) => (
                  <div
                    key={row.agentId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 96px 84px',
                      gap: '8px',
                      padding: '10px 16px',
                      borderBottom: '1px solid var(--border)',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500 }}>{row.name}</span>
                    <span
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 700,
                        fontSize: '12.5px',
                        color: row.wait.lazinessAvgSec > row.wait.lazinessThresholdSec ? '#ef6d6d' : 'var(--text)',
                      }}
                    >
                      {fmtMS(row.wait.lazinessAvgSec)}
                    </span>
                    <span
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 700,
                        fontSize: '12.5px',
                        color: row.wait.longGaps >= 3 ? '#ef6d6d' : 'var(--dim)',
                      }}
                    >
                      {row.wait.longGaps}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ ...cardStyle, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>Sonuç tipine göre bekleme</div>
                  <div style={{ fontSize: '11px', color: 'var(--faint)' }}>Her sonuç sonrası ort. bekleme (dk:sn)</div>
                </div>
                <CsvButton onClick={onCsv2b} small />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 108px 72px',
                  gap: '8px',
                  padding: '9px 16px',
                  borderBottom: '1px solid var(--border)',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '.04em',
                  color: 'var(--faint)',
                }}
              >
                <span>SONUÇ</span>
                <span style={{ textAlign: 'right' }}>ORT. BEKLEME</span>
                <span style={{ textAlign: 'right' }}>ADET</span>
              </div>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {waitByResult.map((r) => (
                  <div
                    key={r.key}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 108px 72px',
                      gap: '8px',
                      padding: '11px 16px',
                      borderBottom: '1px solid var(--border)',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                      <span style={dotStyle(r.color, 9)}></span>
                      <span style={{ fontSize: '12.5px', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.label}
                      </span>
                    </span>
                    <span style={{ textAlign: 'right', fontSize: '13px', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>
                      {r.avg}
                    </span>
                    <span style={{ textAlign: 'right', fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
